'use client';

import { useState } from 'react';
import { clsx } from 'clsx';
import { useSwipe } from '@/lib/useSwipe';
import { SmartImage, SmartVideoPoster } from './Skeleton';

export interface Slide {
  type: 'image' | 'video';
  src: string;
}

interface MediaCarouselProps {
  slides: Slide[];
  /** Accessible label / alt prefix (the section eyebrow). */
  label: string;
  className?: string;
  /** Click the active slide to open it in the shared viewer at this slide index. */
  onOpen?: (index: number) => void;
}

function Arrow({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path
        d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlayBadge() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-on-surface/40 bg-surface/60 backdrop-blur-md">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 text-on-surface" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </div>
  );
}

/**
 * One-slide-at-a-time carousel for images and/or videos. Browse with the arrows;
 * videos show a static poster (first frame) with a play badge and NEVER autoplay.
 * Clicking the active slide calls onOpen so it opens in the shared lightbox.
 */
export default function MediaCarousel({ slides, label, className, onOpen }: MediaCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const go = (delta: number) => setIndex((p) => (p + delta + count) % count);
  const swipe = useSwipe(() => go(1), () => go(-1));

  if (count === 0) return null;

  return (
    <div className={clsx('space-y-stack-md', className)}>
      <div
        className="forest-img-shadow group relative aspect-[4/3] overflow-hidden rounded-sm border border-surface-bright/20 bg-surface-container-low"
        onTouchStart={swipe.onTouchStart}
        onTouchEnd={swipe.onTouchEnd}
      >
        {/* All slides are stacked; images stay mounted (so they preload and never
            flash to the dark container background when navigating). Videos mount
            only while active to avoid decoding every clip at once. */}
        {slides.map((slide, i) => {
          const active = i === index;
          return (
            <div
              key={`${slide.src}-${i}`}
              aria-hidden={!active}
              onClick={active && onOpen ? () => onOpen(i) : undefined}
              className={clsx(
                'absolute inset-0 transition-opacity duration-300',
                active ? 'opacity-100' : 'pointer-events-none opacity-0',
                active && onOpen && 'cursor-pointer'
              )}
            >
              {slide.type === 'image' ? (
                <SmartImage
                  src={slide.src}
                  alt={`${label} ${i + 1}`}
                  fill
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  className="object-cover"
                />
              ) : active ? (
                <>
                  {/* Static poster (first frame) — never autoplays. */}
                  <SmartVideoPoster
                    src={`${encodeURI(slide.src)}#t=0.1`}
                    tabIndex={-1}
                    className="h-full w-full object-cover"
                    aria-label={`${label} ${i + 1}`}
                  />
                  <PlayBadge />
                </>
              ) : null}
            </div>
          );
        })}

        {count > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous"
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Arrow dir="left" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next"
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Arrow dir="right" />
            </button>

            <span className="absolute right-3 top-3 rounded-full bg-surface/70 px-3 py-1 text-label-caps font-headline tracking-widest text-on-surface backdrop-blur-md">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="flex flex-wrap justify-center gap-2">
          {slides.map((s, i) => (
            <button
              key={`${s.src}-${i}`}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index}
              className={clsx(
                'h-1.5 rounded-full transition-all duration-200',
                i === index ? 'w-6 bg-primary' : 'w-1.5 bg-outline hover:bg-on-surface-variant'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
